import React, { useMemo, useEffect } from 'react';

/**
 * Hierarchical project dropdown — Shows ↓ Episodes nested + standalone projects flat.
 *
 * Why this exists:
 *  • A bare `projects.map(<option>)` puts Shows and their Episodes side-by-side with no
 *    visual link, so users would (a) pick a Show and see an empty pipeline, then (b) think
 *    "the system isn't talking to itself".
 *  • Shows are containers — every workspace (MDL, screenplay, tasks, shots) lives inside
 *    Episodes. This component makes that hierarchy visible AND auto-redirects "Show clicks"
 *    to the show's first episode so users never land on an empty page by accident.
 *
 * Usage:
 *   <ProjectHierarchyPicker
 *      projects={projects} value={projectId} onChange={setProjectId}
 *      data-testid="my-project-select" autoSelectFirstEpisode
 *   />
 */
export default function ProjectHierarchyPicker({
  projects = [],
  value,
  onChange,
  className = '',
  autoSelectFirstEpisode = true,
  showStandaloneFirst = true,
  'data-testid': testId,
}) {
  const groups = useMemo(() => {
    const shows = projects.filter(p => p.is_show);
    const byParent = new Map();
    for (const ep of projects.filter(p => p.parent_project_id)) {
      const arr = byParent.get(ep.parent_project_id) || [];
      arr.push(ep);
      byParent.set(ep.parent_project_id, arr);
    }
    // Sort each Show's episodes by episode_number (deterministic across all users).
    // Fallback to created_at when episode_number is missing (legacy episodes).
    for (const arr of byParent.values()) {
      arr.sort((a, b) => {
        const an = a.episode_number ?? 9999;
        const bn = b.episode_number ?? 9999;
        if (an !== bn) return an - bn;
        return (a.created_at || '').localeCompare(b.created_at || '');
      });
    }
    const standalone = projects.filter(p => !p.is_show && !p.parent_project_id);
    const allIds = new Set(projects.map(p => p.id));
    const orphans = projects.filter(p => p.parent_project_id && !allIds.has(p.parent_project_id));
    return { shows, byParent, standalone, orphans };
  }, [projects]);

  const handleChange = (newId) => {
    if (autoSelectFirstEpisode) {
      const picked = projects.find(p => p.id === newId);
      if (picked?.is_show) {
        const eps = groups.byParent.get(picked.id) || [];
        if (eps.length > 0) {
          onChange(eps[0].id);
          return;
        }
      }
    }
    onChange(newId);
  };

  // Pre-select first usable project so the user lands on something with data
  useEffect(() => {
    if (!projects.length || value) return;
    const firstReal = projects.find(p => !p.is_show);
    onChange(firstReal ? firstReal.id : projects[0].id);
  }, [projects, value, onChange]);

  const renderShowGroup = (show) => {
    const eps = groups.byParent.get(show.id) || [];
    return (
      <optgroup key={show.id} label={`📺 ${show.name} (Show — pick an episode)`}>
        <option value={show.id} disabled={eps.length === 0}>
          ↳ all episodes ({eps.length})
        </option>
        {eps.map((ep, i) => (
          <option key={ep.id} value={ep.id}>
            &nbsp;&nbsp;&nbsp;EP{String(ep.episode_number ?? (i + 1)).padStart(2, '0')} · {ep.name}
          </option>
        ))}
      </optgroup>
    );
  };

  return (
    <select value={value || ''} onChange={e => handleChange(e.target.value)}
      data-testid={testId}
      className={className || "h-8 bg-zinc-900 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-3 focus:outline-none min-w-[14rem]"}>
      {showStandaloneFirst && groups.standalone.length > 0 && (
        <optgroup label="── Projects ──">
          {groups.standalone.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </optgroup>
      )}
      {groups.shows.map(renderShowGroup)}
      {!showStandaloneFirst && groups.standalone.length > 0 && (
        <optgroup label="── Projects ──">
          {groups.standalone.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </optgroup>
      )}
      {groups.orphans.length > 0 && (
        <optgroup label="── Orphan Episodes ──">
          {groups.orphans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </optgroup>
      )}
    </select>
  );
}

/**
 * Render a violet "this is a Show — pick an episode" banner with quick-pick chips
 * for every episode under the show. Drop this just below the picker on any page
 * that lets the user select a project (so even if they bypass the auto-redirect,
 * they have a visible escape route).
 */
export function ShowSelectedBanner({ project, projects, onPickEpisode, ctaLabel = 'Add Episode in Projects', testId = 'show-selected-banner' }) {
  if (!project?.is_show) return null;
  const eps = projects.filter(p => p.parent_project_id === project.id);
  return (
    <div className="bg-violet-500/5 border border-violet-500/30 rounded-sm p-3 flex items-start gap-3"
         data-testid={testId}>
      <span className="text-violet-300 text-base leading-none mt-0.5">📺</span>
      <div className="flex-1">
        <p className="text-xs font-medium text-violet-100">
          {project.name} is a Show — pick an episode
        </p>
        <p className="text-[10px] text-zinc-400 mt-0.5">
          Shows are containers; the screenplay, MDL, shots and tasks all live inside individual episodes.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {eps.map((ep, i) => (
            <button key={ep.id} onClick={() => onPickEpisode(ep.id)}
              data-testid={`${testId}-ep-${i}`}
              className="text-[10px] px-2.5 py-1 rounded bg-violet-500/15 hover:bg-violet-500/30 text-violet-200 border border-violet-500/30">
              EP{String(ep.episode_number ?? (i + 1)).padStart(2, '0')} · {ep.name}
            </button>
          ))}
          {eps.length === 0 && (
            <span className="text-[10px] text-zinc-500 italic">
              No episodes yet — create them in Projects → {project.name} → {ctaLabel}.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
